import { useTheme } from '@emotion/react';
import { IconArrowsVertical } from '@hubble/icons';
import { spacing } from '@hubble/tokens/web';
import React__default, { createContext, forwardRef, useMemo, useContext } from 'react';
import { Button } from '../Button/index.js';
import { Flex } from '../Flex/index.js';
import { Message } from '../forms/Message/index.js';
import { isNamedComponent } from '../PageLayout/utils.js';
import { Text } from '../Text/index.js';
import { validComponentNames, validChildren, requiredChildren } from './constants.js';
import { InputCardWrapper, InputCardError, InputCardInput, InputCardActionsWrapper, InputCardLabel, InputCardUtility } from './styles.js';
import { jsx, jsxs } from '@emotion/react/jsx-runtime';

const InputCardContext = createContext({
  name: undefined
});
const handleWheel = event => {
  if (event.currentTarget.type === 'number') {
    event.currentTarget.blur();
  }
};
const Root = ({
  name,
  error,
  children,
  ...props
}) => {
  const validChildrenMap = {};
  React__default.Children.forEach(children, child => {
    const validName = validComponentNames.find(name => {
      return isNamedComponent(child, validChildren[name]);
    });
    if (validName) {
      validChildrenMap[validName] = child;
    } else if (child) {
      console.group('InputCard error');
      console.error('Invalid child:', child);
      console.error(`Only ${Object.keys(validChildren).map(name => validChildren[name]).join(', ')} are allowed. Other children are ignored.`);
      console.groupEnd();
    }
  });
  const {
    VisualElementPicker,
    Input,
    Actions,
    Pair,
    Constraint,
    Utility
  } = validChildrenMap;
  Object.keys(requiredChildren).map(requiredChild => {
    if (!(requiredChild in validChildrenMap)) {
      console.error(`${requiredChild} is required by InputCard.`);
    }
  });
  const contextValue = useMemo(() => ({
    name
  }), [name]);
  return jsx(InputCardContext.Provider, {
    value: contextValue,
    children: jsxs("div", {
      ...props,
      children: [jsxs(InputCardWrapper, {
        htmlFor: name,
        "data-error": Boolean(error),
        children: [jsxs(Flex, {
          alignItems: "center",
          gap: spacing[8],
          rowGap: spacing[16],
          alignSelf: "stretch",
          flexWrap: "wrap",
          justifyContent: "space-between",
          children: [VisualElementPicker, Constraint]
        }), jsxs(Flex.Column, {
          gap: spacing[8],
          alignItems: "start",
          alignSelf: "stretch",
          children: [jsxs(Flex, {
            alignItems: "center",
            gap: spacing[8],
            alignSelf: "stretch",
            children: [Input, Actions]
          }), jsx(Flex, {
            alignItems: "start",
            gap: spacing[4],
            alignSelf: "stretch",
            flexWrap: "wrap",
            children: Pair
          })]
        })]
      }), error && jsx(InputCardError, {
        children: jsx(Message, {
          error: error
        })
      }), Utility]
    })
  });
};
const InputBase = (props, ref) => {
  const {
    name
  } = useContext(InputCardContext);
  const {
    onWheel
  } = props;
  return jsx(InputCardInput, {
    ...props,
    as: "input",
    ref: ref,
    name: name,
    id: name,
    size: "lg",
    onWheel: onWheel || handleWheel
  });
};
const Input = forwardRef(InputBase);
const Actions = ({
  children,
  ...props
}) => {
  const renderChildren = [];
  React__default.Children.forEach(children, (child, index) => {
    if (React__default.isValidElement(child)) {
      renderChildren.push(React__default.cloneElement(child, {
        size: 'sm',
        key: `inputcard-actions-${index}`
      }));
    }
  });
  return jsx(InputCardActionsWrapper, {
    ...props,
    children: renderChildren
  });
};
const Pair = ({
  onSwap,
  children,
  ...props
}) => {
  const theme = useTheme();
  const SWAP_ARIA_LABEL = theme.translate('InputCard.swapButton');
  return jsxs(InputCardLabel, {
    flex: "1 0 0",
    ...props,
    children: [typeof children === 'string' ? jsx(Text.Body, {
      as: "span",
      size: "sm",
      bold: true,
      color: "currentColor",
      children: children
    }) : children, onSwap && jsx(Button.Quaternary, {
      onClick: onSwap,
      size: "xs",
      "aria-label": SWAP_ARIA_LABEL,
      icon: jsx(IconArrowsVertical, {})
    })]
  });
};
const Constraint = ({
  children,
  ...props
}) => {
  const theme = useTheme();
  return jsx(InputCardLabel, {
    children: jsx(Button.Quaternary, {
      size: "xs",
      ...props,
      children: typeof children === 'string' ? jsx(Text.Body, {
        size: "xs",
        bold: true,
        color: theme.colorScheme.content.secondary,
        children: children
      }) : children
    })
  });
};
const Utility = ({
  children
}) => {
  return jsx(InputCardUtility, {
    children: children
  });
};
const InputCard = Object.assign(Root, {
  Input,
  Actions,
  Pair,
  Constraint,
  Utility,
  displayName: 'InputCard'
});
Input.displayName = 'InputCard.Input';
Actions.displayName = 'InputCard.Actions';
Pair.displayName = 'InputCard.Pair';
Constraint.displayName = 'InputCard.Constraint';
Utility.displayName = 'InputCard.Utility';

export { Input, InputCard, InputCardContext };
